import type { ThemeMode } from '../themeMode'

interface ThemeColorValue {
  dark: string
  light: string
}

export const themeColorTokens = {
  background: { dark: '#1e1a1d', light: '#faf6f9' },
  'card-fill-primary': { dark: '#292128', light: '#ffffff' },
  'card-fill-secondary': { dark: '#362e35', light: '#eeeef1' },
  'card-fill-tertiary': { dark: '#372a36', light: '#ededf0' },
  'card-fill-quaternary': { dark: '#483646', light: '#fafafb' },
  'stroke-primary': { dark: '#4d444b', light: '#f0f0f2' },
  'stroke-secondary': { dark: '#372e35', light: '#e7e7ec' },
  /* Field hover border, one step above the resting border in both modes. */
  'stroke-tertiary': { dark: '#6c5b67', light: '#bbbbc9' },
  /* A field's error state: its border and its validation message share this. */
  'input-error': { dark: '#ff8a80', light: '#cc2a19' },
  cta: { dark: '#ffa9fc', light: '#a13b7f' },
  'text-primary': { dark: '#ffffff', light: '#18181f' },
  'text-secondary': { dark: '#d9bfd4', light: '#38383f' },
  caption: { dark: '#867083', light: '#5c5865' },
  'text-caption': { dark: '#a88ca4', light: '#5c5865' },
  'main-secondary': { dark: '#debdd9', light: '#38383f' },

  'color-surfaceItemBackgroundLow': { dark: '#362e35', light: 'var(--card-fill-secondary)' },
  'color-borderMedium': { dark: '#372e35', light: 'var(--stroke-secondary)' },
  'color-hoverPrimary': { dark: '#452e45', light: 'var(--card-fill-secondary)' },
  'color-surfaceContainerBackground': { dark: '#292128', light: 'var(--card-fill-primary)' },
  'category-chip-default-bg': { dark: 'rgba(193, 183, 188, 1)', light: '#443740' },
  'category-chip-default-text': { dark: '#1e1a1d', light: '#ffffff' },
  'cat-chip-text-inverted': { dark: '#ffffff', light: '#ffffff' },
  'cat-chip-detection-and-response-bg': { dark: 'rgba(177, 92, 15, 1)', light: '#1b60cb' },
  'cat-chip-vulnerability-management-bg': { dark: 'rgba(125, 117, 7, 1)', light: '#327ab8' },
  'cat-chip-training-and-awareness-bg': { dark: '#781a7e', light: '#066a5d' },
  'cat-chip-cloud-security-bg': { dark: 'rgba(250, 195, 100, 1)', light: '#216127' },
  'cat-chip-iam-bg': { dark: 'rgba(142, 154, 255, 1)', light: '#714766' },
  'cat-chip-network-security-bg': { dark: 'rgba(71, 116, 130, 1)', light: '#8428bd' },
  'cat-chip-device-management-bg': { dark: 'rgba(219, 219, 219, 1)', light: '#cd3d8c' },
  'cat-chip-mail-and-web-security-bg': { dark: '#072845', light: '#502db9' },
  'cat-chip-application-security-bg': { dark: '#d279df', light: '#48792e' },
  'custom-cat-chip-text-black': { dark: '#000000', light: '#ffffff' },
  'custom-cat-chip-1-bg': { dark: 'rgba(255,235,249,1)', light: '#502db9' },
  'custom-cat-chip-2-bg': { dark: 'rgba(255,224,250,1)', light: '#8428bd' },
  'custom-cat-chip-3-bg': { dark: 'rgba(255,203,253,1)', light: '#a13b7f' },
  'custom-cat-chip-4-bg': { dark: 'rgba(255,169,252,1)', light: '#714766' },
  'custom-cat-chip-5-bg': { dark: 'rgba(236,133,236,1)', light: '#1b60cb' },
  'custom-cat-chip-6-bg': { dark: 'rgba(206,107,207,1)', light: '#327ab8' },
  'custom-cat-chip-7-bg': { dark: 'rgba(176,81,179,1)', light: '#066a5d' },
  'custom-cat-chip-8-bg': { dark: 'rgba(148,55,152,1)', light: '#216127' },
  'custom-cat-chip-9-bg': { dark: 'rgba(127,40,131,1)', light: '#48792e' },
  'custom-cat-chip-10-bg': { dark: 'rgba(112,21,119,1)', light: '#683831' },
  'custom-cat-chip-11-bg': { dark: 'rgba(92,7,99,1)', light: '#c65939' },
  'custom-cat-chip-12-bg': { dark: 'rgba(77,0,83,1)', light: '#998c23' },
  'chart-area-bad': { dark: '#66292d', light: '#f5c6c6' },
  'chart-area-good': { dark: '#325524', light: '#a3dfc0' },
  'chart-card-date': { dark: '#a88ca4', light: '#5c5865' },
  'status-value-good': { dark: '#aed581', light: '#15803d' },
  'status-value-bad': { dark: '#ff8a80', light: '#da2d1b' },
  'sla-line-bad': { dark: '#ff7f6b', light: '#da2d1b' },
  'gauge-track': { dark: '#2f357d', light: '#e7e7ec' },
  'perf-area-fill': { dark: '#573f56', light: '#f8ecf3' },
  'category-perf-area-fill': { dark: '#573f56', light: '#e5c8dd' },
  'chart-axis-text': { dark: '#d0c3cc', light: '#6b6b76' },
  'bench-more-strict': { dark: '#781a7e', light: '#b8399f' },
  'chart-grid': { dark: '#6c5b67', light: '#c4c4ce' },
  'chart-series-1': { dark: '#8e9aff', light: '#7480e8' },
  'chart-series-2': { dark: '#f09543', light: '#e8a048' },
  'chart-series-3': { dark: '#781a7e', light: '#b8399f' },
  'chart-series-4': { dark: '#c77eda', light: '#dc78e3' },
  'chart-series-5': { dark: '#ffde6a', light: '#ffde6a' },
  'nist-identify': { dark: '#8e9aff', light: '#7480e8' },
  'nist-protect': { dark: '#c77eda', light: '#dc78e3' },
  'nist-detect': { dark: '#ffde6a', light: '#ffde6a' },
  'nist-respond': { dark: '#ff7f6b', light: '#ff6047' },
  'nist-recover': { dark: '#99fdc0', light: '#00bf49' },
  'nist-govern': { dark: '#ff9e47', light: '#f09543' },
  'asset-devices': { dark: '#ae4794', light: '#b8399f' },
  'expense-estimated': { dark: '#ff7f6b', light: '#fe5858' },
  'expense-recorded-area': { dark: 'rgba(72, 54, 70, 0.24)', light: 'rgba(201, 201, 207, 0.24)' },
  'chart-cursor': { dark: 'rgba(255, 169, 252, 0.05)', light: 'rgba(161, 59, 127, 0.06)' },
  'sla-chip-bg': { dark: 'rgba(208, 188, 255, 0.16)', light: 'rgba(255, 255, 255, 0.86)' },
  'dropdown-border': { dark: '#4e444b', light: '#dddde4' },
  'dropdown-label': { dark: '#a88ca4', light: '#38383f' },
  'menu-bg': { dark: '#372e35', light: '#ffffff' },
  'menu-item-text': { dark: '#e9e0e4', light: '#18181f' },
  'menu-item-hover': { dark: '#4e444b', light: '#eeeef1' },
  'beta-chip-inactive': { dark: '#debdd9', light: '#a13b7f' },
  'chip-active-bg': { dark: '#3754fc', light: '#3730a3' },
  'button-circle-border': { dark: '#372e35', light: '#e7e7ec' },
  'count-indicator-num': { dark: '#d0c3cc', light: '#6b6b76' },
  'tooltip-bg': { dark: '#573f56', light: '#f5ebf3' },
  'modal-surface': { dark: '#1e1a1d', light: '#ffffff' },
  'tooltip-shadow': { dark: '0 4px 8px 0 rgba(30, 26, 29, 0.20)', light: '0 4px 8px 0 rgba(30, 26, 29, 0.20)' },
  'button-hollow-hover': { dark: '#c477c1', light: '#820d55' },
  'button-hollow-focus': { dark: '#483646', light: '#65254f' },
  'button-hollow-disabled': { dark: '#946592', light: '#d996c3' },
  'calendar-range-endpoint': { dark: '#7c3aed', light: '#a13b7f' },
  'calendar-range-endpoint-hover': { dark: '#6d28d9', light: '#7f2f64' },
  'calendar-range-middle': { dark: '#4e444b', light: '#f8ecf3' },
  'calendar-disabled-day': { dark: '#7f747c', light: '#acacb4' },
  'calendar-action-hover': { dark: 'rgba(255, 255, 255, 0.08)', light: '#eeeef1' },
  'scrollbar-track': { dark: '#292128', light: '#ededf0' },
  'btn-secondary-text': { dark: '#d0c3cc', light: '#3d333c' },
  'btn-secondary-border': { dark: '#4e444b', light: '#ccccd6' },
  'btn-secondary-hover-bg': { dark: '#372e35', light: '#e8e5e7' },
  'btn-secondary-disabled-border': { dark: '#372e35', light: '#e5e5e5' },
  'btn-secondary-disabled-bg': { dark: '#483646', light: 'transparent' },
  'btn-secondary-disabled-text': { dark: '#7f747c', light: '#acacb4' },
  'btn-ghost-focus': { dark: '#ffa9fc', light: '#a13b7f' },
  'avatar-bg': { dark: '#debdd9', light: '#e9c3dd' },
  'avatar-bg-active': { dark: '#573f56', light: '#f1dae9' },
  'avatar-text': { dark: '#3f293e', light: '#7f2f65' },
  'logo-square': { dark: '#781A7E', light: 'transparent' },
  'logo-mark': { dark: '#ffffff', light: '#f1dae9' },
  'toggle-track': { dark: '#998d96', light: '#e2e2e2' },
  'toggle-thumb-bg': { dark: '#372e35', light: '#ffffff' },
  'toggle-thumb-border': { dark: '#4d444b', light: '#e2e2e2' },
  'settings-card-bg': { dark: '#392b39', light: '#ffffff' },
  'label-card-bg': { dark: '#30282f', light: '#ffffff' },
  'label-card-hover': { dark: '#372e35', light: '#fafafb' },
  'label-title-added': { dark: '#ec85ec', light: '#a13b7f' },
  'label-title-removed': { dark: '#ec85ec', light: '#da2d1b' },
  'label-title-sla': { dark: '#f09543', light: '#c94a00' },
  'label-title-custom': { dark: '#ffe082', light: '#976c00' },
  'calendar-bg': { dark: '#372e35', light: '#ffffff' },
  'calendar-day': { dark: '#d0c3cc', light: '#38383f' },
  'input-border-subtle': { dark: 'rgba(255, 255, 255, 0.2)', light: '#dddde4' },
  'checkbox-unchecked': { dark: '#998d96', light: '#5c5865' },
  'checkbox-checked': { dark: '#ffffff', light: '#18181f' },
  'select-chevron': { dark: '#9ca3af', light: '#4a4a54' },
  'calendar-icon': { dark: 'rgba(255, 255, 255, 0.7)', light: '#4a4a54' },
  'devices-popup-bg': { dark: '#393038', light: 'var(--card-fill-primary)' },
  'devices-header-bg': { dark: '#372e35', light: '#f0e5ee' },
  'devices-secondary-text': { dark: '#d0c3cc', light: '#38383f' },
  'devices-row-border': { dark: '#292328', light: 'var(--stroke-primary)' },
  'devices-row-odd-bg': { dark: '#30282f', light: 'var(--card-fill-primary)' },
  'devices-row-hover-bg': { dark: '#392b39', light: 'var(--card-fill-secondary)' },
  'devices-input-bg': { dark: '#261f25', light: 'var(--card-fill-secondary)' },
  'devices-select-border': { dark: '#564852', light: '#dddde4' },
  'devices-exclusion-btn-bg': { dark: '#483646', light: '#eddce6' },
  'devices-empty-cell': { dark: '#939393', light: '#9898af' },
  'btn-tertiary-bg': { dark: '#483646', light: '#eddce6' },
  'aca-zerostate-icon': { dark: '#d9bfd4', light: '#a13b7f' },
  'framework-card-bg': { dark: '#372a36', light: '#ffffff' },
  'tab-inactive': { dark: '#debdd9', light: '#5c5865' },
  'analyzer-border': { dark: '#342f32', light: '#f0f0f2' },
  'analyzer-panel-darker': { dark: '#131013', light: '#ededf0' },
  'analyzer-panel-bg': { dark: '#281e28', light: '#fafafb' },
  'analyzer-chip-text': { dark: '#282624', light: '#18181f' },
  'analyzer-muted-text': { dark: '#585858', light: '#5c5865' },
  'analyzer-control-active': { dark: '#3d3443', light: '#eeeef1' },
  'analyzer-highlight': { dark: 'rgba(208, 188, 255, 0.16)', light: 'rgba(161, 59, 127, 0.10)' },
  'analyzer-highlight-soft': { dark: 'rgba(208, 188, 255, 0.08)', light: 'rgba(161, 59, 127, 0.06)' },
  'analyzer-scrollbar-track': { dark: '#2c242c', light: '#ededf0' },
  'analyzer-scrollbar-thumb': { dark: '#524256', light: '#d8d8df' },
  'analyzer-bar-odd': { dark: '#30262f', light: '#fafafb' },
  'analyzer-bar-even': { dark: 'rgba(208, 188, 255)', light: '#ffffff' },
  'analyzer-field-selected': { dark: '#6c586a', light: '#eeeef1' },
  'analyzer-thead-bg': { dark: 'rgba(87, 63, 86, 0.4)', light: '#f0e5ee' },
  'analyzer-icon-active': { dark: '#590060', light: '#f8ecf3' },
  'analyzer-icon-hover': { dark: '#ec85ec', light: '#eeeef1' },
  'analyzer-venn-neutral': { dark: '#ffffff', light: '#e7e7ec' },
  'framework-hover': { dark: 'rgba(234, 221, 255, 0.08)', light: 'rgba(161, 59, 127, 0.06)' },
  'framework-circle-bg': { dark: '#544152', light: '#ededf0' },

  /* The light-mode nav rail is dark plum, so it cannot share the page's text and
     surface tokens. Dark values match what the rail already renders. */
  'nav-bg': { dark: '#292128', light: '#431935' },
  'nav-border': { dark: '#372e35', light: '#431935' },
  'nav-text': { dark: '#d0c3cc', light: '#f1daea' },
  'nav-text-secondary': { dark: '#d9bfd4', light: '#f1daea' },
  'nav-wordmark': { dark: '#e9e0e4', light: '#f1dae9' },
  'nav-logo': { dark: '#e9e0e4', light: '#f1dae9' },
  'nav-item-hover': { dark: '#452e45', light: 'rgba(255, 255, 255, 0.08)' },
  'nav-item-active-bg': { dark: '#573f56', light: '#ffffff' },
  'nav-item-active-fg': { dark: '#ffffff', light: '#a03b7f' },
  'nav-sub-item-bg': { dark: '#372a36', light: 'rgba(255, 255, 255, 0.08)' },

  /* Accessors in theme.ts that were hardcoded to dark hex, so they rendered
     unchanged in light mode. Dark values are the literals they replaced. */
  'fg-neutral': { dark: '#e9e0e4', light: '#18181f' },
  'fg-neutral-100': { dark: '#cdc4c8', light: '#38383f' },
  'fg-accent': { dark: '#ffa9fc', light: '#a13b7f' },
  'fg-secondary-low': { dark: '#867083', light: '#5c5865' },
  'fg-dusty-rose': { dark: '#d0c3cc', light: '#38383f' },
  'fg-muted-mauve': { dark: '#7f747c', light: '#5c5865' },
  'fg-soft-mauve': { dark: '#a88ca4', light: '#5c5865' },
  'surface-primary-dark': { dark: '#1e1a1d', light: '#faf6f9' },
  'surface-primary-darker': { dark: '#131013', light: '#ffffff' },
  'surface-primary-light': { dark: '#573f56', light: '#ededf0' },
  'surface-item-bg': { dark: '#372a36', light: '#ededf0' },
  'border-low': { dark: '#4d444b', light: '#e7e7ec' },
  /* Letter-avatar placeholder fill. Split from the dusty-rose text colour, which
     the SSM grid was reusing as a background. */
  'avatar-placeholder-bg': { dark: '#d0c3cc', light: '#ededf0' },
  'chip-new-bg': { dark: '#3754fc', light: '#4f46e5' },
  'control-border': { dark: '#998d96', light: '#e7e7ec' },
  'cpi-chip-bg': { dark: '#debdd9', light: '#ededf0' },
  'cpi-chip-text': { dark: '#3f293e', light: '#18181f' },

  /* Restored production dark values (light-theme migration had changed them). */
  'legacy-greyscale-10': { dark: '#211920', light: '#faf6f9' },
  'legacy-text-primary': { dark: '#ffa9fc', light: '#18181f' },
  'legacy-secondary-30': { dark: '#544152', light: '#eeeef1' },
  'legacy-white': { dark: '#ffffff', light: '#a13b7f' },
  'legacy-surface-30': { dark: '#362e35', light: '#f0f0f2' },
  'legacy-primary-light': { dark: '#573f56', light: '#bbbbc9' },
  'legacy-neutral': { dark: '#e9e0e4', light: '#a13b7f' },
  'legacy-6c5b67': { dark: '#6c5b67', light: '#a13b7f' },
  'legacy-secondary-80': { dark: '#d9bfd4', light: '#5c5865' },
  'legacy-background-dark': { dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.14), rgba(255, 169, 252, 0.14)), #1e1a1d', light: '#ffffff' },
  'legacy-alpha-1': { dark: 'rgba(234,221,255,0.08)', light: '#eeeef1' },
  'legacy-neutral-2': { dark: '#e9e0e4', light: '#38383f' },
  'legacy-primary-70': { dark: '#ec85ec', light: '#a13b7f' },
  'legacy-2d2d3b': { dark: '#2d2d3b', light: '#f0f0f2' },
  'legacy-primary-light-2': { dark: '#573f56', light: '#ffffff' },
  'legacy-2c242c': { dark: '#2c242c', light: '#e7e7ec' },
  'legacy-614964': { dark: '#614964', light: '#e7e7ec' },
  'legacy-primary-40': { dark: '#943798', light: '#a13b7f' },
  'legacy-alpha-2': { dark: 'rgba(208,188,255,0.12)', light: 'rgba(161,59,127,0.10)' },
  'legacy-393038': { dark: '#393038', light: '#eeeef1' },
  'legacy-control-border': { dark: '#998d96', light: '#eeeef1' },
  'legacy-border-low': { dark: '#4d444b', light: '#ffffff' },
  'legacy-372f35': { dark: '#372f35', light: '#ffffff' },
  'legacy-261f25': { dark: '#261f25', light: '#e7e7ec' },
  'legacy-261f25-2': { dark: '#261f25', light: '#ffffff' },
  'legacy-control-border-2': { dark: '#998d96', light: '#5c5865' },
  'legacy-greyscale-50': { dark: '#7f747c', light: '#5c5865' },
  'legacy-surface-container-background': { dark: '#292128', light: '#faf6f9' },
  'legacy-372e35': { dark: '#372e35', light: '#a13b7f' },
  'legacy-white-2': { dark: '#ffffff', light: '#5c5865' },
  'legacy-neutral-3': { dark: '#e9e0e4', light: '#5c5865' },
  'legacy-141218': { dark: '#141218', light: '#faf6f9' },
  'legacy-surface-container-background-2': { dark: '#292128', light: '#eeeef1' },
  'legacy-border-low-2': { dark: '#4d444b', light: '#e7e7ec' },
  'legacy-primary-light-3': { dark: '#573f56', light: '#eeeef1' },
  'legacy-border-low-3': { dark: '#4d444b', light: '#eeeef1' },
  'legacy-alpha-3': { dark: 'rgba(208,188,255,0.16)', light: '#eeeef1' },
  'legacy-text-primary-2': { dark: '#ffa9fc', light: '#e7e7ec' },
  'legacy-primary-darker': { dark: '#131013', light: '#ffffff' },
  'legacy-background-medium': { dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.12), rgba(255, 169, 252, 0.12)), #1e1a1d', light: '#ffffff' },
  'legacy-4e444b': { dark: '#4e444b', light: '#ffffff' },
  'legacy-292128': { dark: '#292128', light: '#f0f0f2' },
  'legacy-primary-70-2': { dark: '#ec85ec', light: '#eeeef1' },
  'legacy-primary-20': { dark: '#590060', light: '#a13b7f' },
  'legacy-white-3': { dark: '#ffffff', light: '#ffffff' },
  'legacy-white-4': { dark: '#ffffff', light: '#38383f' },
  'legacy-control-border-3': { dark: '#998d96', light: '#a13b7f' },
  'legacy-background-darker': { dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.05) 0%, rgba(255, 169, 252, 0.05) 100%), #1e1a1d', light: '#ffffff' },
  /* Design review: the toggle shows one shared track; only the active option is filled. */
  'toggle-inactive-bg': { dark: '#372e35', light: 'transparent' },
  'toggle-selected-bg': { dark: '#4e444b', light: '#ffffff' },
  /* Design review: the pale yellow is unreadable on light surfaces. */
  /* #e68900 from the earlier review measures 2.6:1 on white; this clears 4.3:1. */
  'over-performance-indicator': { dark: '#ffe082', light: '#c65939' },
  /* Design review: the loading card needs to read as a filled well, not blend into the card. */
  'initializing-card-bg': { dark: '#292128', light: '#ededf0' },
  'legacy-toast-shadow': { dark: '0 0 12px 0 rgba(0, 0, 0, 0.25)', light: '0 4px 8px 0 rgba(30, 26, 29, 0.20)' },
  'legacy-chart-tooltip-shadow': {
    dark: '4px 4px 12px 0 rgba(41, 33, 40, 0.32)',
    light: '0 4px 8px 0 rgba(30, 26, 29, 0.20)'
  },
  'legacy-surface-container-background-4': { dark: '#292128', light: '#ffffff' },
  'legacy-info-10': { dark: '#3754fc', light: '#7480e8' },
  'legacy-background-darker-2': {
    dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.05) 0%, rgba(255, 169, 252, 0.05) 100%), #1e1a1d',
    light: 'var(--background)'
  },
  'legacy-alpha-4': { dark: 'rgba(30,26,29,0.38)', light: '#ffffff' },
  'legacy-primary-dark': { dark: '#1e1a1d', light: '#ffffff' },
  'legacy-surface-container-background-3': { dark: '#292128', light: '#ededf0' },
  'legacy-control-border-4': { dark: '#998d96', light: '#e7e7ec' },
  'legacy-surface-item-background': { dark: '#372a36', light: '#eeeef1' },
  'legacy-342f32': { dark: '#342f32', light: '#eeeef1' },
  'legacy-483646': { dark: '#483646', light: '#a13b7f' },
  'legacy-text-primary-3': { dark: '#ffa9fc', light: '#ffffff' },
  'legacy-573f56': { dark: '#573f56', light: '#7f2f64' },
  'legacy-neutral100': { dark: '#cdc4c8', light: '#38383f' },
  'legacy-573f56-2': { dark: '#573f56', light: '#e7e7ec' },
  'legacy-392b39': { dark: '#392b39', light: '#e7e7ec' },
  'legacy-surface-50': { dark: '#483646', light: '#f0f0f2' },
  'legacy-6a5371': { dark: '#6a5371', light: '#ededf0' },
  'legacy-surface-item-background-low': { dark: '#362e35', light: '#ffffff' },
  'legacy-primary-dark-2': { dark: '#1e1a1d', light: '#e7e7ec' },
  'legacy-surface-50-2': { dark: '#483646', light: '#eeeef1' },
  'legacy-4e444b-2': { dark: '#4e444b', light: '#f0f0f2' },
  'legacy-alpha-5': { dark: 'rgba(78,68,75,0.5)', light: '#eeeef1' },
  'legacy-alpha-6': { dark: 'rgba(55,46,53,0.5)', light: '#eeeef1' },
  'legacy-surface-50-3': { dark: '#483646', light: '#ffffff' },
  'legacy-342f32-2': { dark: '#342f32', light: '#e7e7ec' },
  'legacy-alpha-7': { dark: 'rgba(208,188,255,0.08)', light: '#eeeef1' },
  'legacy-292128-2': { dark: '#292128', light: '#e7e7ec' },
  'legacy-alpha-8': { dark: 'rgba(255,255,255,0.05)', light: '#eeeef1' },
  'legacy-fbd8f5': { dark: '#fbd8f5', light: '#e7e7ec' },
  'legacy-483646-2': { dark: '#483646', light: '#e7e7ec' },
  'legacy-secondary-80-2': { dark: '#d9bfd4', light: '#acacb4' },


  /* Restored production dark values (light-theme migration had changed them). */
  'legacy-greyscale-10-2': { dark: '#211920', light: '#ffffff' },
  'legacy-573f56-3': { dark: '#573f56', light: '#38383f' },
  'legacy-000000': { dark: '#000000', light: '#18181f' },

  'color-backgroundLight': {
    dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.08), rgba(255, 169, 252, 0.08)), #1e1a1d',
    light: 'var(--card-fill-primary)'
  },
  'color-backgroundMedium': {
    dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.12), rgba(255, 169, 252, 0.12)), #1e1a1d',
    light: 'var(--card-fill-secondary)'
  },
  'color-backgroundDark': {
    dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.14), rgba(255, 169, 252, 0.14)), #1e1a1d',
    light: 'var(--card-fill-tertiary)'
  },
  'color-backgroundDarker': {
    dark: 'linear-gradient(0deg, rgba(255, 169, 252, 0.05) 0%, rgba(255, 169, 252, 0.05) 100%), #1e1a1d',
    light: 'var(--card-fill-quaternary)'
  }
} as const

export type ThemeColorToken = keyof typeof themeColorTokens

export const cssVar = (token: ThemeColorToken): string => `var(--${token})`

export const resolveThemeColor = (token: ThemeColorToken, mode: ThemeMode): string => {
  const value = themeColorTokens[token][mode]
  const ref = /^var\(--([\w-]+)\)$/.exec(value.trim())
  if (ref && ref[1] in themeColorTokens) return resolveThemeColor(ref[1] as ThemeColorToken, mode)
  return value
}

const block = (selector: string, pick: (value: ThemeColorValue) => string): string => {
  const declarations = Object.entries(themeColorTokens)
    .map(([token, value]) => `--${token}:${pick(value)};`)
    .join('')
  return `${selector}{${declarations}}`
}

export const buildThemeVariablesCss = (): string =>
  `${block(':root', (v) => v.dark)}${block(":root[data-theme='light']", (v) => v.light)}`

export const injectThemeVariables = (): void => {
  const id = 'onyxia-theme-variables'
  if (document.getElementById(id)) return
  const style = document.createElement('style')
  style.id = id
  style.textContent = buildThemeVariablesCss()
  document.head.appendChild(style)
}
